import { loadStripe } from '@stripe/stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import type { SubscriptionStatus } from '../types';

// API base URL - empty for local dev (Vite proxy), set VITE_API_URL for production
const API_BASE = import.meta.env.VITE_API_URL || '';

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

export async function getStripe(): Promise<Stripe | null> {
  return stripePromise;
}

/**
 * Fetch current subscription status from backend
 */
export async function getSubscriptionStatus(): Promise<SubscriptionStatus> {
  const response = await fetch(`${API_BASE}/api/stripe/subscription-status`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch subscription status');
  }

  return response.json();
}

/**
 * Get available pricing tiers
 */
export async function getPrices(): Promise<{
  prices: {
    pro: { monthly: PriceInfo; yearly: PriceInfo };
    enterprise: { monthly: PriceInfo; yearly: PriceInfo };
  };
}> {
  const response = await fetch(`${API_BASE}/api/stripe/prices`);
  if (!response.ok) {
    throw new Error('Failed to fetch prices');
  }
  return response.json();
}

interface PriceInfo {
  price_id: string;
  amount: number;
  currency: string;
  interval: string;
}

interface CheckoutOptions {
  priceId: string;
  customerEmail?: string;
}

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
  try {
    const token = localStorage.getItem('sentimentedge-auth');
    if (token) {
      const parsed = JSON.parse(token);
      return parsed?.state?.token || null;
    }
  } catch {
    // Invalid token format
  }
  return null;
}

/**
 * Get headers with authentication
 */
function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function createCheckoutSession(options: CheckoutOptions): Promise<string> {
  const response = await fetch(`${API_BASE}/api/stripe/create-checkout-session`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      price_id: options.priceId,
      success_url: `${window.location.origin}/settings?subscription=success`,
      cancel_url: `${window.location.origin}/settings#subscription`,
      customer_email: options.customerEmail,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create checkout session');
  }

  const { checkout_url } = await response.json();
  return checkout_url;
}

export async function redirectToCheckout(priceId: string, email?: string): Promise<void> {
  try {
    const checkoutUrl = await createCheckoutSession({
      priceId,
      customerEmail: email,
    });

    // Redirect to Stripe Checkout
    window.location.href = checkoutUrl;
  } catch (error) {
    console.error('Checkout error:', error);
    throw error;
  }
}

export async function redirectToPortal(): Promise<void> {
  const response = await fetch(`${API_BASE}/api/stripe/create-portal-session`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      return_url: `${window.location.origin}/settings`,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create portal session');
  }

  const { portal_url } = await response.json();
  window.location.href = portal_url;
}

// Invoice types
export interface Invoice {
  id: string;
  number: string;
  status: 'paid' | 'open' | 'void' | 'uncollectible';
  amount_due: number;
  amount_paid: number;
  currency: string;
  created: string;
  period_start: string | null;
  period_end: string | null;
  pdf_url: string | null;
  hosted_url: string | null;
  product_name: string;
}

export interface UpcomingInvoice {
  amount_due: number;
  currency: string;
  next_payment_date: string | null;
  period_start: string | null;
  period_end: string | null;
}

/**
 * Fetch invoice history from backend
 */
export async function getInvoices(limit: number = 10): Promise<{ invoices: Invoice[]; has_more: boolean }> {
  const response = await fetch(`${API_BASE}/api/stripe/invoices?limit=${limit}`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch invoices');
  }

  return response.json();
}

/**
 * Fetch upcoming invoice
 */
export async function getUpcomingInvoice(): Promise<{ upcoming: UpcomingInvoice | null }> {
  const response = await fetch(`${API_BASE}/api/stripe/upcoming-invoice`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch upcoming invoice');
  }

  return response.json();
}

// Tier ordering for upgrade/downgrade detection
const TIER_ORDER: Record<string, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  early_supporter: 3,
  enterprise: 4,
};

export function isTierUpgrade(currentTier: string, newTier: string): boolean {
  return (TIER_ORDER[newTier] ?? 0) > (TIER_ORDER[currentTier] ?? 0);
}

export function isTierDowngrade(currentTier: string, newTier: string): boolean {
  return (TIER_ORDER[newTier] ?? 0) < (TIER_ORDER[currentTier] ?? 0);
}

/**
 * Change subscription (upgrade or downgrade) for existing subscribers
 */
export async function changeSubscription(
  newTier: string,
  billingPeriod: 'monthly' | 'yearly' = 'monthly'
): Promise<{
  action: 'upgraded' | 'downgrade_scheduled';
  new_tier: string;
  effective_immediately: boolean;
  effective_date?: string | null;
  prorated_amount?: number | null;
  message: string;
}> {
  const response = await fetch(`${API_BASE}/api/stripe/change-subscription`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      new_tier: newTier,
      billing_period: billingPeriod,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to change subscription');
  }

  return response.json();
}

/**
 * Preview a subscription change (get prorated cost before confirming)
 */
export async function previewChange(
  newTier: string,
  billingPeriod: 'monthly' | 'yearly' = 'monthly'
): Promise<{
  amount_due: number;
  proration_charge: number;
  proration_credit: number;
  currency: string;
  current_tier: string;
  new_tier: string;
  days_remaining: number;
  is_upgrade: boolean;
}> {
  const response = await fetch(`${API_BASE}/api/stripe/preview-change`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      new_tier: newTier,
      billing_period: billingPeriod,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to preview subscription change');
  }

  return response.json();
}

/**
 * Create a Stripe Checkout session for a subscription upgrade (prorated)
 */
export async function createUpgradeCheckout(
  newTier: string,
  billingPeriod: 'monthly' | 'yearly' = 'monthly'
): Promise<string> {
  const response = await fetch(`${API_BASE}/api/stripe/upgrade-checkout`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      new_tier: newTier,
      billing_period: billingPeriod,
      success_url: `${window.location.origin}/settings?upgrade=success&tier=${newTier}`,
      cancel_url: `${window.location.origin}/settings?tab=plans`,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create upgrade checkout');
  }

  const data = await response.json();

  // If upgrade was free (no proration needed)
  if (data.action === 'upgraded_free') {
    return 'free';
  }

  return data.checkout_url;
}

/**
 * Complete upgrade after Stripe Checkout payment
 */
export async function completeUpgrade(sessionId: string): Promise<{
  action: string;
  new_tier: string;
  message: string;
  amount_charged?: number;
  next_billing_amount?: number;
  billing_interval?: string;
  next_billing_date?: string | null;
}> {
  const response = await fetch(`${API_BASE}/api/stripe/complete-upgrade?session_id=${sessionId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to complete upgrade');
  }

  return response.json();
}

/**
 * Cancel a pending downgrade
 */
export async function cancelPendingDowngrade(): Promise<{
  success: boolean;
  message: string;
  current_tier: string;
}> {
  const response = await fetch(`${API_BASE}/api/stripe/cancel-downgrade`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to cancel downgrade');
  }

  return response.json();
}

/**
 * Check for paid upgrades that were never finalized (recovery mechanism)
 */
export async function checkPendingUpgrade(): Promise<{
  pending: boolean;
  completed?: boolean;
  action?: string;
  new_tier?: string;
  message?: string;
}> {
  const response = await fetch(`${API_BASE}/api/stripe/check-pending-upgrade`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    return { pending: false };
  }

  return response.json();
}

// Subscription tiers configuration
export const SUBSCRIPTION_TIERS = {
  free: {
    name: 'Free',
    price: 0,
    features: [
      '5 stocks tracked',
      '7 days historical data',
      'Daily sentiment updates',
      'Basic disconnect scores',
    ],
    limits: {
      stocks: 5,
      dailyScans: 5,
      historicalDays: 7,
    },
  },
  early_supporter: {
    name: 'Early Supporter',
    oneTimePrice: 199,
    priceId: import.meta.env.VITE_STRIPE_PRICE_EARLY_SUPPORTER || '',
    lifetime: true,
    features: [
      'Lifetime access',
      '100 stocks tracked',
      '1 year historical data',
      'AI Chat assistant',
      'Backtesting simulator',
      'Pattern detection',
      'Data export',
      'Priority support',
      'All future Pro features',
    ],
    limits: {
      stocks: 100,
      dailyScans: 100,
      historicalDays: 365,
    },
  },
  starter: {
    name: 'Starter',
    monthlyPrice: 49,
    yearlyPrice: 399,
    priceIds: {
      monthly: import.meta.env.VITE_STRIPE_PRICE_STARTER_MONTHLY || '',
      yearly: import.meta.env.VITE_STRIPE_PRICE_STARTER_YEARLY || '',
    },
    features: [
      '25 stocks tracked',
      '30 days historical data',
      'AI Chat assistant',
      'AI sentiment summaries',
      'Email alerts',
    ],
    limits: {
      stocks: 25,
      dailyScans: 25,
      historicalDays: 30,
    },
  },
  pro: {
    name: 'Pro',
    monthlyPrice: 99,
    yearlyPrice: 799,
    priceIds: {
      monthly: import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY || '',
      yearly: import.meta.env.VITE_STRIPE_PRICE_PRO_YEARLY || '',
    },
    features: [
      '100 stocks tracked',
      '1 year historical data',
      'AI Chat assistant',
      'Backtesting simulator',
      'Performance analytics',
      'Pattern detection',
      'Data export',
      'Priority support',
    ],
    limits: {
      stocks: 100,
      dailyScans: 100,
      historicalDays: 365,
    },
  },
  enterprise: {
    name: 'Enterprise',
    contactSales: true,
    features: [
      'Unlimited stocks',
      'Full historical data',
      'AI Chat assistant',
      'Full API access',
      'Advanced backtesting',
      'Dedicated account manager',
      'SLA guarantee',
    ],
    limits: {
      stocks: Infinity,
      dailyScans: Infinity,
      historicalDays: 3650,
    },
  },
};
